// Vivy.js
import React, { useEffect, useState } from 'react';
import './App.css';
import { FaInstagram, FaLinkedin, FaCheckCircle, FaEnvelope, FaWallet, FaTasks, FaRocket, FaRobot, FaChartLine, FaPiggyBank, FaSeedling, FaClock, FaFileAlt, FaShieldAlt, FaBook, FaCalendarAlt, FaHeart, FaLightbulb, FaBullseye, FaChartBar, FaSyncAlt } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import FormStatus from './components/FormStatus';
import LegalModal from './components/LegalModal';
import { API_ENDPOINTS } from './config/api';

const faqs = [
    {
        q: "Is Vivy free?",
        a: "Yes! Vivy offers a free plan with essential features. If you want to unlock advanced tools and extra customization, a premium version will be available too."
    },
    {
        q: "How does Vivy use AI?",
        a: "Vivy uses AI to help you stay on track — from smart budgeting advice to habit suggestions and a motivational chatbot. It's like having a personal assistant in your pocket, always learning and improving with you."
    },
    {
        q: "What makes Vivy different from other apps?",
        a: "Vivy brings finances, productivity, and personal growth together in one calm, modern space — powered by AI and built just for your lifestyle."
    },
    {
        q: "Is my data safe?",
        a: "Absolutely. We take privacy seriously. All your data is encrypted, stored securely, and never shared or sold — period."
    },
    {
        q: "When is Vivy launching?",
        a: "We're getting close! Sign up for early access to be the first to try Vivy, get a 20% discount, and help shape the features you want most."
    },
    // {
    //     q: "Do I need to be good with tech to use Vivy?",
    //     a: "Not at all! Vivy is built to be super friendly and intuitive, even if you're not a tech person. Just tap, track, and grow."
    // },
    {
        q: "What kind of features can I expect?",
        a: "Think smart budgeting, savings goals, habit tracking, daily challenges, productivity tools, and even a fun motivation coach. And more features will keep coming based on what you ask for!"
    },
    {
        q: "Can I use Vivy on multiple devices?",
        a: "Yes! Vivy syncs seamlessly across all your devices. Start tracking your budget on your phone, check your habits on your tablet, and review your progress on your laptop. Everything stays in sync, so you're always up to date."
    },
    {
        q: "How does Vivy protect my financial information?",
        a: "Your privacy and security are our top priorities. All financial data is encrypted end-to-end, stored securely, and never shared with third parties. We use bank-level security measures to keep your information safe, and you have full control over your data at all times."
    },
    {
        q: "How can I contact support?",
        a: "We're here to help! You can reach us at support@vivy-ai.com for any questions, feedback, or assistance you need."
    }
];

const Vivy = () => {
    const [waitlistStatus, setWaitlistStatus] = useState(null);
    const [newsletterStatus, setNewsletterStatus] = useState(null);
    const [openFaqIndex, setOpenFaqIndex] = useState(null);
    const [openModal, setOpenModal] = useState(null);

    useEffect(() => {
        // Add scroll animation
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, { threshold: 0.1 });

        const fadeEls = document.querySelectorAll('.fade-in:not(.faq-item)');
        fadeEls.forEach((el) => observer.observe(el));

        // Fallback: after 2s, force all .fade-in to .visible if not already
        const fallback = setTimeout(() => {
            fadeEls.forEach(el => {
                if (!el.classList.contains('visible')) {
                    el.classList.add('visible');
                }
            });
        }, 2000);

        return () => {
            observer.disconnect();
            clearTimeout(fallback);
        };
    }, []);

    useEffect(() => {
        // Prevent body scroll when modal is open
        if (openModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [openModal]);

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setWaitlistStatus('loading');
        const formData = new FormData(e.target);
        const selectedFeatures = Array.from(formData.getAll('features'));
        
        try {
            // Store in Firestore
            await addDoc(collection(db, 'waitlist'), {
                name: formData.get('name'),
                email: formData.get('email'),
                features: selectedFeatures,
                vision: formData.get('vision'),
                timestamp: serverTimestamp()
            });

            // Send welcome email
            const response = await fetch(API_ENDPOINTS.waitlist, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.get('name'),
                    email: formData.get('email'),
                    features: selectedFeatures,
                    vision: formData.get('vision')
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to send welcome email');
            }

            setWaitlistStatus('success');
            e.target.reset();
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setWaitlistStatus(null);
            }, 5000);
        } catch (error) {
            console.error('Error submitting form:', error);
            setWaitlistStatus('error');
            
            // Clear error message after 5 seconds
            setTimeout(() => {
                setWaitlistStatus(null);
            }, 5000);
        }
    };

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        setNewsletterStatus('loading');
        const email = e.target.querySelector('input[type="email"]').value;
        
        try {
            // Store in Firestore
            await addDoc(collection(db, 'newsletter'), {
                email,
                timestamp: serverTimestamp()
            });

            // Send welcome email
            const response = await fetch(API_ENDPOINTS.newsletter, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to send welcome email');
            }

            setNewsletterStatus('success');
            e.target.reset();
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setNewsletterStatus(null);
            }, 5000);
        } catch (error) {
            console.error('Error subscribing to newsletter:', error);
            setNewsletterStatus('error');
            
            // Clear error message after 5 seconds
            setTimeout(() => {
                setNewsletterStatus(null);
            }, 5000);
        }
    };

    const scrollToWaitlist = () => {
        const waitlistSection = document.querySelector('.vivy-form');
        waitlistSection?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToFeatures = () => {
        const featuresSection = document.querySelector('.vivy-features-grid');
        featuresSection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="vivy-wrapper">
            {/* Status Messages */}
            <FormStatus status={waitlistStatus} />
            <FormStatus status={newsletterStatus} />

            {/* Floating shapes */}
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
            <div className="floating-shape shape-4"></div>
            <div className="floating-shape shape-5"></div>
            <div className="floating-shape shape-6"></div>
            <div className="floating-shape shape-7"></div>
            <div className="floating-shape shape-8"></div>
            <div className="floating-shape shape-9"></div>
            <div className="floating-shape shape-10"></div>
            <div className="floating-shape shape-11"></div>
            <div className="floating-shape shape-12"></div>
            <div className="floating-shape shape-13"></div>
            <div className="floating-shape shape-14"></div>
            <div className="floating-shape shape-15"></div>
            <div className="floating-shape shape-16"></div>
            <div className="floating-shape shape-17"></div>
            <div className="floating-shape shape-18"></div>
            <div className="floating-shape shape-19"></div>
            <div className="floating-shape shape-20"></div>
            <div className="floating-shape shape-21"></div>
            <div className="floating-shape shape-22"></div>
            <div className="floating-shape shape-23"></div>
            <div className="floating-shape shape-24"></div>
            <div className="floating-shape shape-25"></div>
            <div className="floating-shape shape-26"></div>
            <div className="floating-shape shape-27"></div>
            <div className="floating-shape shape-28"></div>
            <div className="floating-shape shape-29"></div>
            <div className="floating-shape shape-30"></div>

            {/* HERO SECTION */}
            <header className="vivy-header">
                <div className="vivy-hero-content">
                    <img 
                        src={`${process.env.PUBLIC_URL}/images/Logo.png`} 
                        alt="Vivy Logo" 
                        className="vivy-logo-top fade-in"
                    />
                    <h1 className="fade-in">Vivy — Your Life, Upgraded.</h1>
                    <h2 className="fade-in">
                        Smart budgeting, habit building, and productivity powered by AI.
                    </h2>
                    <div className="vivy-cta-buttons fade-in">
                        <button className="vivy-btn-primary" onClick={scrollToWaitlist}>Get Early Access</button>
                        <button className="vivy-btn-secondary" onClick={scrollToFeatures}>See How Vivy Works</button>
                    </div>
                    <div className="vivy-hero-download fade-in">
                        <a 
                            href="https://apps.apple.com/eg/app/vivy-ai-life-hub/id6755442616" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="vivy-app-store-button vivy-app-store-button-hero"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                            <div className="vivy-app-store-text">
                                <span className="vivy-app-store-label">Download on the</span>
                                <span className="vivy-app-store-name">App Store</span>
                            </div>
                        </a>
                    </div>
                </div>
                <div className="vivy-hero-mockup fade-in">
                    <div className="vivy-mockup-container">
                        <img
                            src={`${process.env.PUBLIC_URL}/images/vivy-hero.jpeg`}
                            alt="Vivy Dashboard"
                        />
                    </div>
                </div>
            </header>

            {/* SERVICES SECTION */}
            <section className="vivy-section vivy-services-section">
                <h2>Everything You Need in One Place</h2>
                <p className="vivy-section-subtitle">Powerful tools designed to help you live your best life</p>
                <div className="vivy-services-grid">
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaWallet size={32} />
                        </div>
                        <h3>Budget Smarter</h3>
                        <p>AI-powered spending insights, weekly reports, and goal-based savings. Track every transaction, categorize expenses automatically, and get personalized recommendations to save more.</p>
                        <ul className="vivy-service-features">
                            <li>Automatic expense categorization</li>
                            <li>Weekly spending reports</li>
                            <li>Savings goal tracking</li>
                            <li>AI spending insights</li>
                        </ul>
                    </div>
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaTasks size={32} />
                        </div>
                        <h3>Build Better Habits</h3>
                        <p>Smart habit tracking with streaks, reminders, and difficulty adjustments. Build lasting routines with AI-powered suggestions tailored to your lifestyle and goals.</p>
                        <ul className="vivy-service-features">
                            <li>Streak tracking & motivation</li>
                            <li>Smart reminders</li>
                            <li>Difficulty adjustments</li>
                            <li>AI habit recommendations</li>
                        </ul>
                    </div>
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaRocket size={32} />
                        </div>
                        <h3>Stay Productive</h3>
                        <p>Daily challenges, routines, AI suggestions, and focus timers. Boost your productivity with personalized routines and time management tools.</p>
                        <ul className="vivy-service-features">
                            <li>Focus timer & sessions</li>
                            <li>Daily challenges</li>
                            <li>Productivity routines</li>
                            <li>AI-powered suggestions</li>
                        </ul>
                    </div>
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaBook size={32} />
                        </div>
                        <h3>Journal & Reflect</h3>
                        <p>Reflect on your day with guided journaling prompts. Capture thoughts, track mood, and gain insights into your emotional patterns over time.</p>
                        <ul className="vivy-service-features">
                            <li>Guided journaling prompts</li>
                            <li>Mood tracking</li>
                            <li>Emotional insights</li>
                            <li>Daily reflection reminders</li>
                        </ul>
                    </div>
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaCalendarAlt size={32} />
                        </div>
                        <h3>Plan & Organize</h3>
                        <p>Organize your tasks and schedule with an intuitive planner. Set priorities, manage deadlines, and never miss an important event or goal.</p>
                        <ul className="vivy-service-features">
                            <li>Task management</li>
                            <li>Calendar integration</li>
                            <li>Priority setting</li>
                            <li>Deadline reminders</li>
                        </ul>
                    </div>
                    <div className="vivy-service-card fade-in">
                        <div className="vivy-service-icon">
                            <FaHeart size={32} />
                        </div>
                        <h3>Cycle Tracker</h3>
                        <p>Track your menstrual cycle with privacy-first design. Get predictions, insights, and personalized health recommendations based on your cycle data.</p>
                        <ul className="vivy-service-features">
                            <li>Cycle predictions</li>
                            <li>Health insights</li>
                            <li>Privacy-first design</li>
                            <li>Personalized recommendations</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* PRICING PLANS SECTION */}
            <section className="vivy-section vivy-pricing-section">
                <h2>Choose Your Plan</h2>
                <p className="vivy-section-subtitle">Limited time launch pricing — Lock in these rates forever</p>
                <div className="vivy-pricing-grid">
                    <div className="vivy-pricing-card fade-in">
                        <h3>Free Plan</h3>
                        <div className="vivy-price">
                            <span className="vivy-price-amount">$0</span>
                        </div>
                        <div className="vivy-price-yearly">Always free</div>
                        <ul className="vivy-pricing-features">
                            <li><FaCheckCircle /> Basic budgeting</li>
                            <li><FaCheckCircle /> 3 habits</li>
                            <li><FaCheckCircle /> 3 goals</li>
                            <li><FaCheckCircle /> Limited AI suggestions</li>
                            <li><FaCheckCircle /> Basic analytics</li>
                            <li><FaCheckCircle /> Standard support</li>
                        </ul>
                        <button className="vivy-btn-secondary" onClick={scrollToWaitlist}>Get Started</button>
                    </div>
                    <div className="vivy-pricing-card vivy-pricing-card-featured fade-in">
                        <div className="vivy-featured-badge">Most Popular</div>
                        <h3>Pro Plan</h3>
                        <div className="vivy-price">
                            <div className="vivy-price-original">
                                <span className="vivy-price-original-amount">$9.99</span>
                                <span className="vivy-price-original-period">/mo</span>
                            </div>
                            <div className="vivy-price-discounted">
                                <span className="vivy-price-amount">$3.99</span>
                                <span className="vivy-price-period">/mo</span>
                            </div>
                            <div className="vivy-discount-badge">60% OFF</div>
                        </div>
                        <div className="vivy-price-yearly">
                            <span className="vivy-price-original-inline">$99.99/year</span>
                            <span className="vivy-price-discounted-inline">$24.99/year</span>
                            <span className="vivy-discount-badge-inline">75% OFF</span>
                        </div>
                        <ul className="vivy-pricing-features">
                            <li><FaCheckCircle /> Unlimited habits & goals</li>
                            <li><FaCheckCircle /> Full budgeting analytics</li>
                            <li><FaCheckCircle /> Complete AI assistant</li>
                            <li><FaCheckCircle /> Smart challenges & routines</li>
                            <li><FaCheckCircle /> Siri & Shortcuts integration</li>
                            <li><FaCheckCircle /> Advanced insights & reports</li>
                            <li><FaCheckCircle /> Priority support</li>
                            <li><FaCheckCircle /> Early access to new features</li>
                        </ul>
                        <button className="vivy-btn-primary" onClick={scrollToWaitlist}>Get Early Access</button>
                    </div>
                    <div className="vivy-pricing-card vivy-pricing-card-lifetime fade-in">
                        <div className="vivy-featured-badge vivy-lifetime-badge">Best Value</div>
                        <h3>Lifetime</h3>
                        <div className="vivy-price">
                            <div className="vivy-price-original">
                                <span className="vivy-price-original-amount">$199.99</span>
                            </div>
                            <div className="vivy-price-discounted">
                                <span className="vivy-price-amount">$49.99</span>
                            </div>
                            <div className="vivy-discount-badge">75% OFF</div>
                        </div>
                        <div className="vivy-price-yearly">One-time payment</div>
                        <ul className="vivy-pricing-features">
                            <li><FaCheckCircle /> Everything in Pro</li>
                            <li><FaCheckCircle /> Lifetime updates & upgrades</li>
                            <li><FaCheckCircle /> Priority support forever</li>
                            <li><FaCheckCircle /> Exclusive lifetime features</li>
                            <li><FaCheckCircle /> No subscription ever</li>
                            <li><FaCheckCircle /> Early access to all features</li>
                            <li><FaCheckCircle /> Custom AI training</li>
                            <li><FaCheckCircle /> Personal onboarding session</li>
                            <li><FaCheckCircle /> Direct feedback channel</li>
                            <li><FaCheckCircle /> Beta feature access</li>
                        </ul>
                        <button className="vivy-btn-primary" onClick={scrollToWaitlist}>Get Early Access</button>
                    </div>
                </div>
            </section>

            {/* FEATURES SECTION */}
            <section className="vivy-section vivy-features-section">
                <h2>Powerful Features</h2>
                <p className="vivy-section-subtitle">Everything you need to take control of your life</p>
                <div className="vivy-features-grid fade-in" id="vivy-features-grid">
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaRobot size={24} />
                        </div>
                        <h4>AI Assistant "Vivy"</h4>
                        <p>Your personal AI companion for motivation, advice, and insights</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaChartLine size={24} />
                        </div>
                        <h4>Smart Insights</h4>
                        <p>AI-powered analysis of your spending, habits, and productivity patterns</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaPiggyBank size={24} />
                        </div>
                        <h4>Savings Plans</h4>
                        <p>Set goals, track progress, and get personalized savings recommendations</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaSeedling size={24} />
                        </div>
                        <h4>Habit Builder</h4>
                        <p>Build lasting routines with streak tracking and smart reminders</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaClock size={24} />
                        </div>
                        <h4>Focus Mode</h4>
                        <p>Stay productive with customizable focus timers and session tracking</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaFileAlt size={24} />
                        </div>
                        <h4>Weekly Summaries</h4>
                        <p>Get AI-generated summaries of your week's progress and achievements</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaShieldAlt size={24} />
                        </div>
                        <h4>Privacy-First Design</h4>
                        <p>Your data is encrypted, secure, and never shared with third parties</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaChartBar size={24} />
                        </div>
                        <h4>Analytics Dashboard</h4>
                        <p>Visual insights into your spending, habits, and productivity trends</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaBullseye size={24} />
                        </div>
                        <h4>Goal Tracking</h4>
                        <p>Set and achieve financial, health, and personal development goals</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaLightbulb size={24} />
                        </div>
                        <h4>AI Recommendations</h4>
                        <p>Personalized suggestions for habits, savings, and productivity improvements</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaSyncAlt size={24} />
                        </div>
                        <h4>Cross-Device Sync</h4>
                        <p>Access your data seamlessly across all your devices</p>
                    </div>
                    <div className="vivy-feature-item">
                        <div className="vivy-feature-icon">
                            <FaCalendarAlt size={24} />
                        </div>
                        <h4>Smart Planner</h4>
                        <p>Organize tasks, events, and deadlines with intelligent scheduling</p>
                    </div>
                </div>
            </section>

            <section className='flex'>

                <section className="vivy-section">
                    <div className="fade-in" style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', marginBottom: '2.5rem' }}>
                        <h2>Be First. Get Perks.</h2>
                    </div>
                    <form className="vivy-form fade-in" onSubmit={handleWaitlistSubmit}>
                        <div style={{ color: 'var(--on-background)', fontWeight: 600, fontSize: '1.15rem', marginBottom: '0.5rem' }}>
                            Join the Vivy waitlist for exclusive early access and <b style={{ color: 'var(--primary-color)' }}>20% off</b> when we launch.
                        </div>
                        <div style={{ color: 'var(--on-surface-variant)', fontSize: '1rem', marginBottom: '1rem' }}>
                            Help shape the future of lifestyle apps — and get rewarded for it.
                        </div>
                        <input type="text" name="name" placeholder="Your Name" required />
                        <input type="email" name="email" placeholder="Your Email" required />
                        <label className="vivy-label">What features matter most to you?</label>
                        <div className="vivy-checkbox-group">
                            <label><input type="checkbox" name="features" value="Smart Budgeting" /> Smart Budgeting</label>
                            <label><input type="checkbox" name="features" value="Habit Tracker" /> Habit Tracker</label>
                            <label><input type="checkbox" name="features" value="Daily Challenges" /> Daily Challenges</label>
                            <label><input type="checkbox" name="features" value="Saving Goals" /> Saving Goals</label>
                            <label><input type="checkbox" name="features" value="Spending Insights" /> Spending Insights</label>
                            <label><input type="checkbox" name="features" value="Gamified Productivity" /> Gamified Productivity</label>
                            <label><input type="checkbox" name="features" value="Mood Tracker" /> Mood Tracker</label>
                            <label><input type="checkbox" name="features" value="Focus Timer" /> Focus Timer</label>
                        </div>
                        <textarea
                            name="vision"
                            placeholder="Share your vision for the perfect life management app..."
                            rows="4"
                        />
                        <button 
                            type="submit" 
                            className={waitlistStatus === 'loading' ? 'loading' : ''}
                            disabled={waitlistStatus === 'loading'}
                        >
                            {waitlistStatus === 'loading' ? (
                                <>
                                    <div className="spinner"></div>
                                    <span>Claiming...</span>
                                </>
                            ) : (
                                'Claim My Spot'
                            )}
                        </button>
                    </form>
                </section>

                {/* FAQ SECTION */}
                <section className="vivy-section">
                    <h2>Frequently Asked Questions</h2>
                    <div className="faq-container">
                        {faqs.map((faq, idx) => {
                            const isOpen = openFaqIndex === idx;
                            return (
                                <div 
                                    key={idx} 
                                    className={`faq-item ${isOpen ? 'is-open' : ''}`}
                                >
                                    <button 
                                        className="faq-button"
                                        onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="faq-question-text">{faq.q}</span>
                                        <span className={`faq-icon ${isOpen ? 'is-open' : ''}`}>
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </span>
                                    </button>
                                    <div className={`faq-content ${isOpen ? 'is-open' : ''}`}>
                                        <div className="faq-content-inner">
                                            <p>{faq.a}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </section>

            {/* APP STORE DOWNLOAD SECTION */}
            <section className="vivy-section vivy-download-section">
                <div className="vivy-download-container">
                    <div className="vivy-download-content">
                        <h2>Download Vivy Today</h2>
                        <p className="vivy-download-subtitle">
                            Transform your life with AI-powered budgeting, habit building, and productivity tools. 
                            Join thousands of users who are already upgrading their daily routines.
                        </p>
                        <div className="vivy-download-buttons">
                            <a 
                                href="https://apps.apple.com/eg/app/vivy-ai-life-hub/id6755442616" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="vivy-app-store-button"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                </svg>
                                <div className="vivy-app-store-text">
                                    <span className="vivy-app-store-label">Download on the</span>
                                    <span className="vivy-app-store-name">App Store</span>
                                </div>
                            </a>
                        </div>
                        <p className="vivy-download-note">
                            Available now on iOS • Coming soon to Android
                        </p>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="vivy-footer">
                <div className="footer-content">
                    <div className="footer-main">
                        <div className="footer-brand">
                            <p className="footer-made-by">Made by <a href="https://hady.netlify.app" target="_blank" rel="noopener noreferrer">Hady Korshem</a></p>
                        </div>
                        <div className="footer-links">
                            <button 
                                type="button"
                                className="footer-link-button"
                                onClick={() => setOpenModal('privacy')}
                            >
                                Privacy Policy
                            </button>
                            <button 
                                type="button"
                                className="footer-link-button"
                                onClick={() => setOpenModal('terms')}
                            >
                                Terms of Service
                            </button>
                            <button 
                                type="button"
                                className="footer-link-button"
                                onClick={() => setOpenModal('licensing')}
                            >
                                Licensing
                            </button>
                            <button 
                                type="button"
                                className="footer-link-button"
                                onClick={() => setOpenModal('opensource')}
                            >
                                Open-Source Acknowledgements
                            </button>
                            <button 
                                type="button"
                                className="footer-link-button"
                                onClick={() => setOpenModal('thirdparty')}
                            >
                                Third-party Libraries
                            </button>
                        </div>
                        <div className="footer-socials">
                            <a href="https://instagram.com/vivy.app" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <FaInstagram size={24} />
                            </a>
                            <a href="https://tiktok.com/@yourusername" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                <SiTiktok size={24} />
                            </a>
                            <a href="https://www.linkedin.com/company/vivy-app/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                                <FaLinkedin size={24} />
                            </a>
                            <a href="mailto:support@vivy-ai.com" aria-label="Support Email">
                                <FaEnvelope size={24} />
                            </a>
                        </div>
                    </div>
                    <form className="footer-newsletter" onSubmit={handleNewsletterSubmit}>
                        <input type="email" placeholder="Your email for Vivy updates" required />
                        <button 
                            type="submit"
                            className={newsletterStatus === 'loading' ? 'loading' : ''}
                            disabled={newsletterStatus === 'loading'}
                        >
                            Subscribe
                            <span style={{ marginLeft: 8, fontSize: '1.2em' }}>→</span>
                        </button>
                    </form>
                    <p className="footer-copyright">
                        © {new Date().getFullYear()} Vivy. All rights reserved.
                    </p>
                </div>
            </footer>

            {/* LEGAL MODALS */}
            <LegalModal 
                isOpen={openModal === 'privacy'} 
                onClose={() => setOpenModal(null)}
                title="Privacy Policy"
            >
                <h3>Vivy Privacy Policy</h3>
                <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)' }}>Effective Date: November 18, 2025</p>
                <p>Vivy ("we," "our," or "the App") values your privacy. This Privacy Policy explains how we collect, use, and share your personal information when you use our app, services, and features. By using Vivy, you agree to the practices described in this policy.</p>
                
                <h4>1. Information We Collect</h4>
                <p>We collect information you provide directly and information generated through app usage:</p>
                <h4>A. Personal Information</h4>
                <ul>
                    <li>Name, email address, profile information when you sign up or use the app.</li>
                    <li>Account credentials if using Firebase Authentication.</li>
                </ul>
                <h4>B. Usage Data</h4>
                <ul>
                    <li>App activity, including habit tracking, goals, focus sessions, journaling, cycle tracking, and planner events.</li>
                    <li>Chat messages and AI interactions.</li>
                </ul>
                <h4>C. Financial Data</h4>
                <ul>
                    <li>Spending entries, savings goals, budget tracking, and related analytics.</li>
                </ul>
                <h4>D. Health Data</h4>
                <ul>
                    <li>Cycle tracker data and optional wellness information provided by you.</li>
                </ul>
                <h4>E. Device and Technical Information</h4>
                <ul>
                    <li>Device type, operating system, IP address, and app usage statistics.</li>
                </ul>

                <h4>2. How We Use Your Information</h4>
                <ol>
                    <li>Provide and improve Vivy features, including habit tracking, financial insights, AI chat, and daily summaries.</li>
                    <li>Analyze app usage to enhance performance and user experience.</li>
                    <li>Send important updates, notifications, or reminders.</li>
                    <li>Protect against fraud, abuse, or illegal activity.</li>
                </ol>
                <h4>AI Use</h4>
                <p>We use AI models (OpenAI) to process your data for generating daily summaries, habit/focus/financial insights, and motivational responses. No AI model stores your personal information permanently outside our secure systems.</p>

                <h4>3. How We Share Your Information</h4>
                <p>We do not sell or rent your information. We may share data with service providers like Firebase and OpenAI, for legal requirements, or as aggregated, non-identifiable analytics.</p>

                <h4>4. Data Storage and Security</h4>
                <p>All user data is stored securely on Firebase Firestore and Google Cloud infrastructure. AI requests use the OpenAI API over HTTPS. We apply technical and organizational safeguards, but you are responsible for keeping your credentials confidential.</p>

                <h4>5. Your Rights</h4>
                <p>Depending on your location, you may have rights to access, correct, delete, or export your data, and to withdraw consent. Contact itshadyy@gmail.com to exercise these rights.</p>

                <h4>6. Children's Privacy</h4>
                <p>Vivy is not directed at children under 13. If we learn we collected data from a child, we will delete it. Contact us if you believe this has occurred.</p>

                <h4>7. Third-Party Services</h4>
                <ul>
                    <li>Firebase – data storage, authentication, analytics.</li>
                    <li>OpenAI – processes AI requests for chat, summaries, and insights.</li>
                    <li>Remote Config – manages dynamic app configurations.</li>
                </ul>
                <p>We encourage you to review the privacy policies of these services.</p>

                <h4>8. Updates to This Privacy Policy</h4>
                <p>We may update this policy periodically. Changes will be posted in the app with an updated effective date. Please review this policy from time to time.</p>

                <h4>9. Contact Us</h4>
                <p>If you have questions about this Privacy Policy or how your data is handled, contact:</p>
                <div className="legal-modal-contact-box">
                    <h4>Vivy Privacy Team</h4>
                    <p>Email: <a href="mailto:itshadyy@gmail.com">itshadyy@gmail.com</a></p>
                </div>
            </LegalModal>

            <LegalModal 
                isOpen={openModal === 'terms'} 
                onClose={() => setOpenModal(null)}
                title="Terms of Service"
            >
                <h3>Vivy Terms of Service</h3>
                <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)' }}>Effective Date: November 2025</p>
                <p>By using Vivy, you agree to the following terms:</p>

                <h4>Use of the App</h4>
                <ul>
                    <li>You may use Vivy for personal, non-commercial purposes.</li>
                    <li>You agree not to misuse the app or attempt to access unauthorized areas.</li>
                </ul>

                <h4>Subscriptions & Payments</h4>
                <ul>
                    <li>Vivy offers subscription plans (Monthly, Yearly, Lifetime).</li>
                    <li>Payments are processed via the App Store. All fees are non-refundable unless required by law.</li>
                </ul>

                <h4>Content & AI</h4>
                <ul>
                    <li>Vivy provides AI-powered suggestions for habits, finance, and wellness.</li>
                    <li>AI outputs are for guidance and personal use only, not professional advice.</li>
                </ul>

                <h4>Limitation of Liability</h4>
                <ul>
                    <li>Vivy and Hady Korshem are not responsible for any damages arising from app usage.</li>
                    <li>We make no warranties regarding uninterrupted or error-free service.</li>
                </ul>

                <h4>Changes to Terms</h4>
                <p>We may update these Terms. Users will be notified within the app.</p>

                <h4>Contact</h4>
                <div className="legal-modal-contact-box">
                    <h4>Vivy Support</h4>
                    <p>Email: <a href="mailto:support@vivy-ai.com">support@vivy-ai.com</a></p>
                </div>
            </LegalModal>

            <LegalModal 
                isOpen={openModal === 'licensing'} 
                onClose={() => setOpenModal(null)}
                title="Licensing"
            >
                <h3>Vivy Licensing</h3>
                <p>Vivy is a personal project by Hady Korshem. All rights reserved.</p>

                <h4>License to Use</h4>
                <ul>
                    <li>You are granted a non-exclusive, non-transferable license to use the Vivy app for personal purposes.</li>
                    <li>You may not redistribute, modify, or sell the app without explicit permission.</li>
                </ul>

                <h4>Intellectual Property</h4>
                <p>All content, designs, and code in Vivy are the property of Hady Korshem.</p>

                <h4>Contact</h4>
                <div className="legal-modal-contact-box">
                    <h4>Hady Korshem</h4>
                    <p>Email: <a href="mailto:itshadyy@gmail.com">itshadyy@gmail.com</a></p>
                    <p>Website: <a href="https://hady.netlify.app" target="_blank" rel="noopener noreferrer">https://hady.netlify.app</a></p>
                    <p>Phone: <a href="tel:+18573224782">+1 (857) 322-4782</a></p>
                </div>
            </LegalModal>

            <LegalModal 
                isOpen={openModal === 'opensource'} 
                onClose={() => setOpenModal(null)}
                title="Open-source Acknowledgements"
            >
                <h3>Open-source Acknowledgements</h3>
                <p>Vivy uses open-source software. We thank the following projects:</p>

                <div style={{ marginTop: '24px' }}>
                    <h4>Flutter</h4>
                    <p>UI framework for building natively compiled applications</p>
                    <p><a href="https://flutter.dev" target="_blank" rel="noopener noreferrer">https://flutter.dev</a></p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>Firebase</h4>
                    <p>Backend services for authentication, database, and remote configuration</p>
                    <p><a href="https://firebase.google.com" target="_blank" rel="noopener noreferrer">https://firebase.google.com</a></p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>Provider</h4>
                    <p>State management library for Flutter</p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>Dart packages</h4>
                    <p>Various Dart packages including http, shared_preferences, and more</p>
                </div>

                <div className="legal-modal-contact-box" style={{ marginTop: '32px' }}>
                    <h4>Full Licenses</h4>
                    <p>Full licenses for all dependencies are available in the app via:</p>
                    <p style={{ fontWeight: 600, color: 'var(--primary-color)', marginTop: '12px' }}>Settings → Legal → Licenses</p>
                </div>
            </LegalModal>

            <LegalModal 
                isOpen={openModal === 'thirdparty'} 
                onClose={() => setOpenModal(null)}
                title="Third-party Libraries"
            >
                <h3>Third-party Libraries / Services</h3>
                <p>Vivy integrates with the following third-party services:</p>

                <div style={{ marginTop: '24px' }}>
                    <h4>OpenAI API</h4>
                    <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)', marginBottom: '8px' }}>AI-powered chat & suggestions</p>
                    <p>Provides AI functionality for generating daily summaries, habit/focus/financial insights, and motivational responses.</p>
                    <p><a href="https://openai.com" target="_blank" rel="noopener noreferrer">https://openai.com</a></p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>Firebase</h4>
                    <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)', marginBottom: '8px' }}>Authentication, Firestore database, Remote Config</p>
                    <p>Handles user authentication, data storage, and dynamic app configuration management.</p>
                    <p><a href="https://firebase.google.com" target="_blank" rel="noopener noreferrer">https://firebase.google.com</a></p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>CocoaPods / iOS Plugins</h4>
                    <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)', marginBottom: '8px' }}>For iOS native integration</p>
                    <p>Enables native iOS functionality and integrations.</p>
                    <p><a href="https://cocoapods.org" target="_blank" rel="noopener noreferrer">https://cocoapods.org</a></p>
                </div>

                <div style={{ marginTop: '24px' }}>
                    <h4>Various Dart/Flutter packages</h4>
                    <p style={{ fontStyle: 'italic', color: 'var(--on-surface-variant)', marginBottom: '8px' }}>Listed in pubspec.yaml</p>
                    <p>Includes packages for HTTP requests, local storage, state management, and more.</p>
                </div>

                <div className="legal-modal-contact-box" style={{ marginTop: '32px', background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>⚠️</span> Important Notice
                    </h4>
                    <p>Each third-party service is subject to its own terms and privacy policies. We encourage you to review these policies to understand how your data is handled by these services.</p>
                </div>
            </LegalModal>
        </div>
    );
};

export default Vivy;